"use client";

import { RefreshCw, ShieldAlert, X } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useTranslations } from "next-intl";
import { ANCHOR_REFS } from "@/lib/launcher-anchors";
import { PREVIEW_VISITOR_LIMIT } from "@/lib/use-visitor-auth";
import { cn } from "@/lib/utils";

interface AuthErrorPopupProps {
  /** Open / closed. Driven by the shell — pops open when the visitor
   *  taps the launcher while auth is failing; closes on the X chip or
   *  when the underlying error resolves. */
  open: boolean;
  /** Raw `VisitorSession.error` string used to pick the right copy.
   *  `turnstile_failed:*` → Turnstile-specific message; anything else
   *  → generic handshake-error copy. */
  errorCode: string | null;
  /** Visitor tapped Retry. Shell bumps `sessionEpoch` to re-fire the
   *  handshake (and Turnstile challenge, if enabled). The popup
   *  should close immediately on retry — the launcher spinner takes
   *  over as the "working" indicator. */
  onRetry: () => void;
  /** Visitor tapped the X. Hides the popup but keeps the underlying
   *  error — they can re-summon it by tapping the launcher again. */
  onDismiss: () => void;
  /** Anchored above the launcher on the same side. */
  position?: "left" | "right";
  /** Anchor to the host `<div>` instead of viewport — set by the
   *  React-component embed (`WidgetEnv.embedded`). */
  embedded?: boolean;
}

/**
 * Floating auth-error popup pinned above the launcher FAB — surfaces
 * a clear, actionable failure state when `useVisitorAuth` couldn't
 * complete the handshake (Turnstile rejected, BE error, network
 * blocked, etc.).
 *
 * Sits in the SAME spot as `LauncherPeek` (above the closed launcher,
 * same side) — the visitor can't message either way, so a small
 * popup is the right surface (vs replacing the panel which would
 * imply "you've opened the messenger but it's broken").
 *
 * Two flavours by error class:
 *   - **`turnstile_failed:*`** — shield icon + Turnstile-specific copy
 *     ("Couldn't verify your browser — network or ad-blocker issue").
 *   - **everything else** — generic alert icon + "Couldn't connect".
 */
export function AuthErrorPopup({
  open,
  errorCode,
  onRetry,
  onDismiss,
  position = "right",
  embedded = false,
}: AuthErrorPopupProps) {
  const t = useTranslations("authError");
  const onLeft = position === "left";
  const isTurnstile = errorCode?.startsWith("turnstile_failed:") ?? false;
  // Terminal preview state: the test-visitor quota is spent, so there is nothing to retry.
  const isLimit = errorCode === PREVIEW_VISITOR_LIMIT;
  const title = isLimit
    ? t("visitorLimitTitle")
    : isTurnstile
      ? t("turnstileTitle")
      : t("genericTitle");
  const body = isLimit
    ? t("visitorLimitBody")
    : isTurnstile
      ? t("turnstileBody")
      : t("genericBody");

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          // Same spring-up entrance as LauncherPeek so the popup
          // feels like part of the same family of floating
          // affordances above the FAB.
          initial={{ opacity: 0, y: 18, scale: 0.88 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 8, scale: 0.96 }}
          transition={{ type: "spring", stiffness: 440, damping: 20 }}
          className={cn(
            // One below the launcher so an overlapping tap still lands
            // on the launcher; both well above any host-page chrome.
            "bottom-24 z-2147483599 w-[300px]",
            embedded ? "absolute" : "fixed",
            onLeft ? "left-4" : "right-4",
          )}
          // Measurement anchor: the iframe shell reads this box so the closed frame
          // grows to contain the popup (it is clipped otherwise). Harmless elsewhere.
          data-wx-popup=""
          ref={ANCHOR_REFS.popup}
          role="alert"
          aria-live="polite"
          aria-label={title}
        >
          <div
            className={cn(
              "relative rounded-wx-lg border border-wx-border bg-wx-bg",
              "p-4 pr-9 text-wx-fg",
              "shadow-[0_16px_40px_rgba(0,0,0,0.16)]",
            )}
          >
            <button
              type="button"
              onClick={onDismiss}
              aria-label={t("dismiss")}
              className={cn(
                "absolute top-2 right-2 flex h-6 w-6 items-center justify-center rounded-full",
                "text-wx-fg-muted transition-colors hover:bg-wx-bg-elevated hover:text-wx-fg",
              )}
            >
              <X size={12} />
            </button>
            <div className="flex items-start gap-3">
              <div
                className={cn(
                  "flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
                  "bg-wx-danger/10 text-wx-danger",
                )}
              >
                {isTurnstile || isLimit ? (
                  <ShieldAlert size={16} aria-hidden="true" />
                ) : (
                  <RefreshCw size={16} aria-hidden="true" />
                )}
              </div>
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <p className="text-sm font-medium text-wx-fg">{title}</p>
                <p className="text-xs leading-relaxed text-wx-fg-muted">{body}</p>
              </div>
            </div>
            {!isLimit && (
              <div className="mt-3 flex justify-end">
                <button
                  type="button"
                  onClick={onRetry}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium",
                    "bg-wx-primary text-wx-primary-fg transition-colors",
                    "hover:bg-wx-primary-hover",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-wx-primary focus-visible:ring-offset-2 focus-visible:ring-offset-wx-bg",
                  )}
                >
                  <RefreshCw size={12} aria-hidden="true" />
                  {t("retry")}
                </button>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
